<template>
	<div class="content-wrapper">
		<!-- Content -->
		<div class="spinner-border spinner-border-lg text-primary load-center" role="status" v-if="isLoading">
			<span class="visually-hidden">Loading...</span>
		</div>

		<div class="container flex-grow-1 container-p-y" v-if="!isLoading" style="padding-top: 0px !important;">
			<!-- Section 1: Employee Selection -->
			<div class="row mb-5">
				<div class="col-12">
					<div class="card">
						<div class="card-header bg-label-secondary">
							<h5 class="card-title mb-0">{{ $t('trainings.create.selectEmployee') }}</h5>
						</div>
						<div class="card-body pt-5">
							<!-- Search input -->
							<div class="form-floating form-floating-outline mb-4" v-if="!selectedEmployee">
								<input
									type="text"
									class="form-control"
									:placeholder="$t('trainings.create.searchEmployee')"
									v-model="employeeSearch"
									@input="onEmployeeSearch"
									autocomplete="off"
								/>
								<label>{{ $t('trainings.create.searchEmployee') }}</label>
							</div>

							<!-- Dropdown results -->
							<ul class="list-unstyled mb-0" v-if="!selectedEmployee && employeeSearch && employees.length > 0">
								<li
									class="mb-3 row-grey"
									v-for="(employee, index) in employees"
									:key="index"
									@click="selectEmployee(employee)"
									style="cursor: pointer;"
								>
									<div class="d-flex align-items-center">
										<div class="avatar me-2">
											<img :src="`https://ui-avatars.com/api/?name=${employee.firstName}+${employee.lastName}&background=666cff&color=fff`" alt="Avatar" class="rounded-circle" />
										</div>
										<div>
											<h6 class="mb-0">{{ employee.firstName }} {{ employee.lastName }}</h6>
											<small>{{ employee.email }}</small>
										</div>
									</div>
								</li>
							</ul>

							<!-- Selected employee chip -->
							<div v-if="selectedEmployee" class="d-flex align-items-center">
								<div class="avatar me-3">
									<img :src="`https://ui-avatars.com/api/?name=${selectedEmployee.firstName}+${selectedEmployee.lastName}&background=666cff&color=fff`" alt="Avatar" class="rounded-circle" />
								</div>
								<div>
									<h6 class="mb-0">{{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}</h6>
									<small class="text-muted">{{ selectedEmployee.email }}</small>
								</div>
								<button class="btn btn-sm btn-outline-secondary ms-auto" @click="clearEmployee()">
									<i class="ri-close-line me-1"></i> {{ $t('common.actions.change') }}
								</button>
							</div>

							<!-- Add employee button -->
							<div class="mt-4" v-if="!selectedEmployee">
								<button type="button" class="btn btn-outline-primary btn-sm" @click="resetEmployeeForm()" data-bs-toggle="modal" data-bs-target="#createEmployee">
									<i class="ri-user-add-line me-1"></i> {{ $t('employees.profile.addEmployee') }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Section 2: Training Catalog + Cart -->
			<div v-if="selectedEmployee">
				<!-- Search Bar -->
				<div class="row mb-4">
					<div class="col-12">
						<div class="card">
							<div class="card-body">
								<div class="input-group input-group-merge">
									<span class="input-group-text">
										<i class="ri-search-line"></i>
									</span>
									<input
										type="text"
										class="form-control"
										:placeholder="$t('trainings.create.searchPlaceholder')"
										v-model="searchTraining"
										autocomplete="off"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Category Tabs (full width) -->
				<div class="row mb-4">
					<div class="col-12">
						<ul class="nav nav-tabs">
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeCategory === 'all' }"
									@click="activeCategory = 'all'" href="javascript:void(0)">
									{{ $t('trainings.create.categoryAll') }}
								</a>
							</li>
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeCategory === 'security' }"
									@click="activeCategory = 'security'" href="javascript:void(0)">
									{{ $t('trainings.create.categorySecurity') }}
								</a>
							</li>
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeCategory === 'safety' }"
									@click="activeCategory = 'safety'" href="javascript:void(0)">
									{{ $t('trainings.create.categorySafety') }}
								</a>
							</li>
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeCategory === 'dgr' }"
									@click="activeCategory = 'dgr'" href="javascript:void(0)">
									{{ $t('trainings.create.categoryDGR') }}
								</a>
							</li>
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeCategory === 'packs' }"
									@click="activeCategory = 'packs'" href="javascript:void(0)">
									{{ $t('trainings.create.categoryPacks') }}
								</a>
							</li>
						</ul>
					</div>
				</div>

				<!-- Training cards + Cart -->
				<div class="row">
				<div class="col-xs-12 col-12 col-lg-8 col-xl-8">
					<div class="row">
						<div class="col-xxl-6 col-lg-6 pb-20" v-for="(training, index) in filteredTrainings" :key="index">
							<div class="card h-100">
								<img class="card-img-top" :src="training.previewUrl[0].url" alt="Formation" />

								<a
									:href="training.publicLink"
									target="_blank"
									style="position: absolute; top: 10px; right: 10px; background: rgba(255,255,255,0.9); width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; text-decoration: none; color: #666cff; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
									data-bs-toggle="tooltip" :data-bs-title="$t('trainings.create.moreInfo')"
								>
									i
								</a>

								<div class="card-body">
									<div class="row mb-6 g-6">
										<div class="col-6">
											<div class="d-flex">
												<div class="avatar flex-shrink-0 me-4">
													<span class="avatar-initial rounded-3 bg-label-primary">
														<i class="ri-calendar-line ri-24px"></i>
													</span>
												</div>
												<div>
													<h6 class="mb-0 text-nowrap fw-normal">
														{{ training.validityInYears }} an{{ training.validityInYears > 1 ? 's' : '' }}
													</h6>
													<small>{{ $t('common.validity') }}</small>
												</div>
											</div>
										</div>
										<div class="col-6">
											<div class="d-flex">
												<div class="avatar flex-shrink-0 me-4">
													<span class="avatar-initial rounded-3 bg-label-primary">
														<i class="ri-time-line ri-24px"></i>
													</span>
												</div>
												<div>
													<h6 class="mb-0 text-nowrap fw-normal">
														{{
															training.durationInHours < 1
															? Math.round(training.durationInHours * 60) + ' minute' + (Math.round(training.durationInHours * 60) > 1 ? 's' : '')
															: formatDuration(training.durationInHours)
														}}
													</h6>
													<small>{{ $t('common.duration') }}</small>
												</div>
											</div>
										</div>
									</div>

									<!-- Already in cart -->
									<button type="button" class="btn  btn-label-secondary waves-effect w-100" v-if="isInCart(training)" disabled>
										<i class="ri-check-fill me-1"></i> {{ $t('trainings.create.addedToCart') }}
									</button>
									<!-- Available: configure & add -->
									<a
										href="javascript:void(0);"
										class="btn btn-primary w-100"
										@click="openConfigModal(training)"
										v-else-if="training.isAvailable && !training.displayWarning"
									>
										{{ $t('trainings.create.configureAndAdd') }}
									</a>
									<!-- Warning -->
									<a
										href="javascript:void(0);"
										class="btn btn-primary w-100"
										@click="showWarningModal()"
										v-else-if="training.displayWarning"
									>
										{{ $t('trainings.create.requestAccess') }}
									</a>
									<!-- Unavailable -->
									<a href="javascript:void(0);" class="btn btn-label-secondary w-100" v-else disabled="true">
										{{ $t('trainings.create.comingSoon') }}
									</a>
								</div>
							</div>
						</div>

						<div class="card" v-if="filteredTrainings.length === 0">
							<div class="card-body text-center">
								<p class="mb-0 text-muted">{{ $t('trainings.create.noCategoryResults') }}</p>
							</div>
						</div>
					</div>
				</div>

				<!-- Cart Sidebar -->
				<div class="col-xs-12 col-12 col-xl-4">
					<div class="border rounded-4 p-5 mb-4" style="position: sticky; top: 20px;">
						<h6>{{ $t('trainings.create.cartTitle') }}</h6>
						<p class="text-muted" v-if="cart.length === 0">{{ $t('trainings.create.cartEmpty') }}</p>

						<div v-for="(item, index) in cart" :key="index" class="mb-3 pb-3" :class="{ 'border-bottom': index < cart.length - 1 }">
							<div class="d-flex justify-content-between align-items-start">
								<div>
									<strong>{{ item.training.title }}</strong>
									<br><small class="text-muted">{{ item.type === 'renewal' ? $t('common.trainingTypes.renewal') : $t('common.trainingTypes.initial') }} - {{ getLanguageName(item.language) }}</small>
									<br v-if="item.orderNumber"><small class="text-muted" v-if="item.orderNumber">PO: {{ item.orderNumber }}</small>
								</div>
								<div class="text-end ms-3" style="white-space: nowrap;">
									<span v-if="!hideTrainingsPrices">{{ formatPrice(item.price) }}</span>
									<button class="btn btn-icon btn-sm btn-outline-danger ms-2" @click="removeFromCart(index)">
										<i class="ri-delete-bin-line"></i>
									</button>
								</div>
							</div>
						</div>

						<hr v-if="cart.length > 0" />
						<dl class="row mb-0" v-if="cart.length > 0 && !hideTrainingsPrices">
							<dt class="col-6 text-heading">Total</dt>
							<dd class="col-6 fw-medium text-heading text-end mb-0">{{ formatPrice(totalPrice) }}</dd>
						</dl>

						<div class="d-grid mt-4">
							<button class="btn btn-primary" @click="submitOrder()" :disabled="cart.length === 0 || isSubmitting">
								<span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
								<span v-if="cart.length === 0">{{ $t('trainings.create.order') }}</span>
								<span v-else-if="hideTrainingsPrices">{{ $t('trainings.create.orderTrainings') }}</span>
								<span v-else>{{ $t('trainings.create.orderFor', { price: formatPrice(totalPrice), unit: '' }) }}</span>
							</button>
						</div>
					</div>
				</div>
				</div>
			</div>

			<!-- Modal: Config Training -->
			<div class="modal fade" id="configTrainingModal" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered modal-lg">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title" v-if="configuringTraining">{{ $t('trainings.create.configure') }} : {{ configuringTraining.title }}</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body" v-if="configuringTraining">
							<!-- Language -->
							<h6 class="mb-3">{{ $t('trainings.create.languageStep') }}</h6>
							<div class="mb-4">
								<select class="form-select" v-model="configForm.language">
									<option disabled value="">{{ $t('trainings.create.selectLanguage') }}</option>
									<option
										v-for="lang in configuringTraining.availableLanguages"
										:key="lang"
										:value="lang"
									>
										{{ getLanguageName(lang) }}
									</option>
								</select>
							</div>

							<!-- Type -->
							<h6 class="mb-3">{{ $t('trainings.create.typeStep') }}</h6>
							<div class="row gy-3 mb-4">
								<div class="col-md">
									<div class="form-check custom-option custom-option-icon">
										<label class="form-check-label custom-option-content" for="configRadioInitial">
											<span class="custom-option-body">
												<i class="ri-play-circle-line"></i>
												<span class="custom-option-title mb-2"> {{ $t('common.trainingTypes.initial') }} </span>
												<small>{{ $t('trainings.create.initialDescription') }}</small>
											</span>
											<input
												name="configTypeRadio"
												class="form-check-input"
												type="radio"
												value="initial"
												id="configRadioInitial"
												v-model="configForm.type"
											/>
										</label>
									</div>
								</div>
								<div class="col-md">
									<div class="form-check custom-option custom-option-icon">
										<label class="form-check-label custom-option-content" for="configRadioRenewal">
											<span class="custom-option-body">
												<i class="ri-loop-left-line"></i>
												<span class="custom-option-title mb-2"> {{ $t('common.trainingTypes.renewal') }} </span>
												<small>{{ $t('trainings.create.renewalDescription') }}</small>
											</span>
											<input
												name="configTypeRadio"
												class="form-check-input"
												type="radio"
												value="renewal"
												id="configRadioRenewal"
												v-model="configForm.type"
											/>
										</label>
									</div>
								</div>
							</div>

							<!-- Order Number -->
							<div class="mb-4" v-if="displayOrderNumber">
								<h6 class="mb-3">{{ $t('trainings.create.internalNumber') }}</h6>
								<input type="text" class="form-control" placeholder="PO12" v-model="configForm.orderNumber" />
							</div>

							<!-- DGR Variant -->
							<div class="mb-4" v-if="configuringTraining.certificateType === 'dgr'">
								<h6 class="mb-3">{{ displayOrderNumber ? '4' : '3' }}. {{ $t('trainings.create.functionChoice') }}</h6>
								<select class="form-select" v-model="configForm.variant" @change="onVariantChange">
									<option disabled>{{ $t('trainings.create.selectFunction') }}</option>
									<option value="generic" selected>{{ $t('trainings.create.genericVersion') }}</option>
									<option value="agent_fret" v-if="configuringTraining.formationName === 'DGR_7.4'">{{ $t('trainings.create.freightAgent') }}</option>
									<option value="agent_galerie" v-if="configuringTraining.formationName === 'DGR_7.4'">{{ $t('trainings.create.galleryAgent') }}</option>
									<option value="agent_piste" v-if="configuringTraining.formationName === 'DGR_7.4'">{{ $t('trainings.create.rampAgent') }}</option>
								</select>

								<div class="d-flex align-items-center mt-3" v-if="!hideTrainingsPrices">
									<div class="avatar avatar-md">
										<div class="avatar-initial bg-label-primary rounded-3">
											<i class="ri-price-tag-3-line ri-24px"></i>
										</div>
									</div>
									<div class="ms-4">
										<p class="mb-0">
											{{ $t('trainings.create.trainingPrice', { price: configPrice, unit: billingMethod === 'invoices' ? '€ HT' : ' crédits' }) }}
										</p>
									</div>
								</div>
							</div>

							<!-- Security Check -->
							<div class="mb-2" v-if="configForm.displaySecurityCheck">
								<div class="form-check form-check-primary mt-2">
									<input class="form-check-input" type="checkbox" id="configSecurityCheck" v-model="configForm.securityCheckAcknowledged" />
									<label class="form-check-label" for="configSecurityCheck" style="font-size: 12px;">
										{{ $t('trainings.create.backgroundCheckWarning') }}
									</label>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('common.actions.cancel') }}</button>
							<button
								class="btn btn-primary"
								@click="addToCart()"
								:disabled="!configForm.type || !configForm.securityCheckAcknowledged"
							>
								{{ $t('trainings.create.addToCart') }}
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Modal: Create Employee -->
			<div class="modal fade" id="createEmployee" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">
							<div class="text-center mb-6">
								<h4 class="mb-2">{{ $t('employees.profile.addEmployee') }}</h4>
							</div>

							<form id="addEmployeeForm" class="row g-5" onsubmit="return false">
								<div class="col-6">
									<div class="form-floating form-floating-outline">
										<input type="text" id="modalNewFirstName" name="modalNewFirstName" class="form-control" v-model="form.firstName" autocomplete="off" />
										<label for="modalNewFirstName">{{ $t('employees.profile.firstName') }}</label>
										<small v-if="errors.firstName" class="text-danger">{{ errors.firstName }}</small>
									</div>
								</div>

								<div class="col-6">
									<div class="form-floating form-floating-outline">
										<input type="text" id="modalNewLastName" name="modalNewLastName" class="form-control" v-model="form.lastName" autocomplete="off" />
										<label for="modalNewLastName">{{ $t('employees.profile.lastName') }}</label>
										<small v-if="errors.lastName" class="text-danger">{{ errors.lastName }}</small>
									</div>
								</div>

								<div class="col-12">
									<div class="form-floating form-floating-outline">
										<input type="email" id="modalNewEmail" name="modalNewEmail" class="form-control" v-model="form.email" autocomplete="off" />
										<label for="modalNewEmail">{{ $t('employees.profile.email') }}</label>
										<small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
									</div>
								</div>

								<div class="col-md-12 col-12">
									<div class="form-floating form-floating-outline">
										<input type="text" id="modalNewBirthDate" placeholder="DD/MM/YYYY" class="form-control" @input="handleDateInput" v-model="form.birthDate" autocomplete="off" />
										<label for="modalNewBirthDate">{{ $t('employees.profile.birthDate') }}</label>
										<small v-if="errors.birthDate" class="text-danger">{{ errors.birthDate }}</small>
									</div>
								</div>

								<div class="col-md-12 col-12">
									<div class="input-group input-group-merge">
										<div class="form-floating form-floating-outline">
											<input type="text" id="modalNewSsn" name="modalNewSsn" class="form-control" placeholder="1 85 05 78 006 084 36" autocomplete="off" @input="handleSsnInput" v-model="form.socialSecurityNumber" />
											<label for="modalNewSsn">{{ $t('common.fields.socialSecurityNumber') }}</label>
											<small v-if="errors.socialSecurityNumber" class="text-danger">{{ errors.socialSecurityNumber }}</small>
										</div>
										<span class="input-group-text" data-bs-toggle="tooltip" :data-bs-title="$t('employees.ssnInfo')">
											<i class="ri-information-line"></i>
										</span>
									</div>
								</div>

								<div class="col-md-12 col-12" v-if="displayAgencies">
									<div class="col-md">
										<div class="form-check custom-option custom-option-icon">
											<select class="form-select" v-model="form.agency">
												<option value="" selected>{{ $t('employees.profile.selectBranch') }}</option>
												<option v-for="agency in agencies" :key="agency" :value="agency">
													{{ agency }}
												</option>
											</select>
										</div>
									</div>
								</div>

								<div class="col-12 d-flex flex-wrap justify-content-center gap-4 row-gap-4">
									<button type="submit" class="btn btn-primary" @click="createEmployee()">{{ $t('common.actions.save') }}</button>
									<button type="button" class="btn btn-outline-secondary btn-reset" data-bs-dismiss="modal" aria-label="Close">
										{{ $t('common.actions.cancel') }}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>

			<!-- Modal: Feature Introduction -->
			<div class="modal fade" id="featureIntroModal" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered modal-simple modal-add-new-cc">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">
							<div class="text-center">
								<i class="ri-shopping-cart-2-line" style="font-size: 45px; color: #666cff;"></i>
								<h4 class="mt-4 mb-2">{{ $t('trainings.create.featureIntroTitle') }}</h4>
								<p class="text-muted">
									{{ $t('trainings.create.featureIntroDesc1') }}
								</p>
								<p class="text-muted">
									{{ $t('trainings.create.featureIntroDesc2') }}
								</p>
								<button class="btn btn-primary mt-2" data-bs-dismiss="modal">{{ $t('trainings.create.featureIntroGotIt') }}</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Modal: Display Warning -->
			<div class="modal fade" id="displayWarningIndividual" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">
							<div class="text-center">
								<i class="ri-id-card-line" style="font-size: 45px;"></i>
								<p class="mt-5 mb-5">
									<strong>{{ $t('trainings.create.warningTitle') }}</strong>
								</p>
								<p>{{ $t('trainings.create.warningClassified') }}</p>
								<p>{{ $t('trainings.create.warningObligations') }}</p>
								<p>{{ $t('trainings.create.warningBlocked') }}</p>
								<p>
									{{ $t('trainings.create.warningContact') }}<br/>
									<a href="mailto:contact@pika-aero.com">contact@pika-aero.com</a><br />
									{{ $t('trainings.create.warningActivate') }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

		</div>
		<!-- / Content -->

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import { Tooltip } from "bootstrap";
import { formatNIR, isValidNIR, normalizeNIR } from "@/helpers/nir";

export default {
	name: "TrainingCreateIndividual",

	data() {
		return {
			// Loading
			isLoading: true,
			isSubmitting: false,

			// Company
			company: null,
			billingMethod: "credits",
			hasDiscount: false,

			// Feature flags
			displayAgencies: false,
			displayOrderNumber: false,
			hideTrainingsPrices: false,
			agencies: [],

			// Employee selection
			employeeSearch: "",
			employeeSearchTimeout: null,
			employees: [],
			selectedEmployee: null,

			// Training catalog
			trainings: [],
			activeCategory: "all",
			searchTraining: "",

			// Config modal
			configuringTraining: null,
			configOriginalPrice: null,
			configPrice: 0,
			configForm: {
				language: "fr",
				type: null,
				orderNumber: null,
				variant: null,
				securityCheckAcknowledged: true,
				displaySecurityCheck: false,
			},

			// Cart
			cart: [],

			// Create employee form
			form: {
				agency: "",
				firstName: "",
				lastName: "",
				email: "",
				birthDate: null,
				socialSecurityNumber: "",
				role: "trainee"
			},
			errors: {},
		};
	},

	methods: {
		// --- Employee ---
		onEmployeeSearch() {
			if (this.employeeSearchTimeout) clearTimeout(this.employeeSearchTimeout);
			this.employeeSearchTimeout = setTimeout(() => {
				this.fetchEmployees();
			}, 300);
		},

		selectEmployee(employee) {
			this.selectedEmployee = employee;
			this.employeeSearch = "";
			this.employees = [];
		},

		clearEmployee() {
			this.selectedEmployee = null;
			this.employeeSearch = "";
			this.employees = [];
		},

		// --- Training Config ---
		openConfigModal(training) {
			this.configuringTraining = training;
			this.configOriginalPrice = training.price;
			this.configPrice = training.price;
			this.configForm = {
				language: training.availableLanguages?.includes("fr") ? "fr" : (training.availableLanguages?.[0] || "fr"),
				type: null,
				orderNumber: null,
				variant: training.certificateType === "dgr" ? "generic" : null,
				securityCheckAcknowledged: !training.formationName?.includes("11.2.3."),
				displaySecurityCheck: training.formationName?.includes("11.2.3."),
			};

			const modal = new window.bootstrap.Modal(document.getElementById("configTrainingModal"));
			modal.show();
		},

		onVariantChange() {
			if (!this.configuringTraining || this.configuringTraining.formationName !== "DGR_7.4") return;

			if (this.configForm.variant === "agent_galerie") {
				if (this.billingMethod === "credits")
					this.configPrice = 17;
				else {
					if (this.company.name === "TRIANGLE ROISSY")
						this.configPrice = 80;
					else
						this.configPrice = 85;
				}
			} else {
				this.configPrice = this.configOriginalPrice;
			}
		},

		addToCart() {
			if (this.displayOrderNumber && !this.configForm.orderNumber) {
				return alert(this.$t('trainings.create.orderNumberRequired'));
			}

			this.cart.push({
				training: this.configuringTraining,
				formationName: this.configuringTraining.formationName,
				type: this.configForm.type,
				language: this.configForm.language,
				orderNumber: this.configForm.orderNumber,
				variant: this.configForm.variant,
				price: this.configPrice,
			});

			const modal = window.bootstrap.Modal.getInstance(document.getElementById("configTrainingModal"));
			if (modal) modal.hide();
			this.configuringTraining = null;
		},

		removeFromCart(index) {
			this.cart.splice(index, 1);
		},

		isInCart(training) {
			return this.cart.some(item => item.formationName === training.formationName);
		},

		// --- Submit ---
		async submitOrder() {
			this.isSubmitting = true;

			try {
				await HTTP.post('/trainings/assign-multiple-trainings', {
					employeeId: this.selectedEmployee._id,
					formations: this.cart.map(item => ({
						formationName: item.formationName,
						type: item.type,
						language: item.language,
						orderNumber: item.orderNumber || null,
						variant: item.variant || null,
					})),
				});

				this.$router.push("/trainings/success");
			} catch (error) {
				this.isSubmitting = false;
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		// --- Warning Modal ---
		showWarningModal() {
			const modalElement = document.getElementById("displayWarningIndividual");
			let modal = window.bootstrap.Modal.getInstance(modalElement);
			if (!modal) modal = new window.bootstrap.Modal(modalElement);
			modal.show();
		},

		// --- Feature Intro ---
		showFeatureIntroIfNeeded() {
			const key = 'pika_individual_intro_seen';
			if (!localStorage.getItem(key)) {
				localStorage.setItem(key, 'true');
				this.$nextTick(() => {
					const modal = new window.bootstrap.Modal(document.getElementById('featureIntroModal'));
					modal.show();
				});
			}
		},

		// --- Shared helpers ---
		formatPrice(price) {
			return this.billingMethod === "invoices" ? price + "€ HT" : price + " crédits";
		},

		formatDuration(hours) {
			const fullHours = Math.floor(hours);
			const minutes = Math.round((hours - fullHours) * 60);

			if (minutes === 0) return `${fullHours} heures`;
			return `${fullHours}h${minutes}`;
		},

		getLanguageName(code) {
			const languages = {
				'fr': 'Français',
				'en': 'English',
				'es': 'Español',
				'de': 'Deutsch'
			};
			return languages[code] || code;
		},

		initTooltips() {
			const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
			tooltipTriggerList.forEach((el) => {
				const existingTooltip = Tooltip.getInstance(el);
				if (existingTooltip) existingTooltip.dispose();
			});

			this.$nextTick(() => {
				const newTooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
				newTooltipTriggerList.forEach((el) => {
					new Tooltip(el);
				});
			});
		},

		// --- API calls ---
		async fetchCompany() {
			try {
				const response = await HTTP('/company');
				this.company = response.data.company;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async fetchFeatureFlag() {
			const response = await HTTP.get("/company/flags");
			this.displayAgencies = response.data.displayAgencies;
			this.displayOrderNumber = response.data.displayOrderNumber;
			this.agencies = response.data.agencies;
			this.hideTrainingsPrices = response.data.hideTrainingsPrices;
		},

		async fetchEmployees() {
			try {
				const response = await HTTP('/company/employees', { params: { limit: 50, search: this.employeeSearch } });
				this.employees = response.data.employees;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async fetchTrainings() {
			try {
				const response = await HTTP('/trainings');
				this.trainings = response.data.formations;
				this.billingMethod = response.data.billingMethod ? response.data.billingMethod : "credits";
				this.hasDiscount = response.data.hasDiscount;
				this.isLoading = false;
				this.$nextTick(() => this.showFeatureIntroIfNeeded());
				this.initTooltips();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		// --- Employee creation ---
		handleDateInput(event) {
			let input = event.target.value;
			input = input.replace(/\D/g, '');

			if (input.length <= 2) {
				this.form.birthDate = input;
			} else if (input.length <= 4) {
				this.form.birthDate = `${input.slice(0, 2)}/${input.slice(2)}`;
			} else {
				this.form.birthDate = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`;
			}
		},

		handleSsnInput(event) {
			this.form.socialSecurityNumber = formatNIR(event.target.value);
		},

		validateEmail(email) {
			const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return re.test(email);
		},

		validateForm() {
			this.errors = {};
			if (!this.form.firstName) this.errors.firstName = this.$t('validation.firstNameRequired');
			if (!this.form.lastName) this.errors.lastName = this.$t('validation.lastNameRequired');
			if (!this.form.email || !this.validateEmail(this.form.email)) this.errors.email = this.$t('validation.emailInvalid');
			if (!this.form.birthDate) this.errors.birthDate = this.$t('validation.birthDateRequired');
			if (this.form.socialSecurityNumber && !isValidNIR(this.form.socialSecurityNumber)) this.errors.socialSecurityNumber = this.$t('validation.invalidSocialSecurityNumber');
			return Object.keys(this.errors).length === 0;
		},

		resetEmployeeForm() {
			this.form = { agency: "", firstName: "", lastName: "", email: "", birthDate: null, socialSecurityNumber: "", role: "trainee" };
			this.errors = {};
		},

		async createEmployee() {
			const validated = this.validateForm();
			if (!validated) return;

			if (this.displayAgencies && this.form.agency === "")
				return alert(this.$t('trainings.create.selectBranchRequired'));

			const payload = { ...this.form };

			if (!payload.birthDate) delete payload.birthDate;

			if (!this.displayAgencies) payload.agency = null;

			if (payload.socialSecurityNumber) payload.socialSecurityNumber = normalizeNIR(payload.socialSecurityNumber);
			else delete payload.socialSecurityNumber;

			const savedEmail = this.form.email;

			try {
				await HTTP.post('/company/employees/create', payload);

				document.activeElement?.blur();

				const modal = window.bootstrap.Modal.getInstance(document.getElementById("createEmployee"));
				try { modal.hide(); } catch (err) { console.log('[Training] Unable to hide modal'); }

				setTimeout(() => {
					document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
					document.body.style.overflow = '';
					document.body.style.paddingRight = '';
					document.body.classList.remove('modal-open');
					window.scrollTo(0, 0);
				}, 500);

				// Auto-select the newly created employee
				const response = await HTTP('/company/employees', { params: { limit: 50, search: savedEmail } });
				const newEmployee = response.data.employees.find(e => e.email === savedEmail);
				if (newEmployee) {
					this.selectedEmployee = newEmployee;
					this.employeeSearch = "";
				}

				this.resetEmployeeForm();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},
	},

	computed: {
		filteredTrainings() {
			let filtered = this.trainings;

			if (this.activeCategory === 'packs') {
				filtered = filtered.filter(t => t.title && t.title.includes('+'));
			} else if (this.activeCategory !== 'all') {
				filtered = filtered.filter(t => t.certificateType === this.activeCategory);
			}

			// Filtre selon la recherche
			if (this.searchTraining && this.searchTraining.trim() !== '') {
				const searchTerm = this.searchTraining.toLowerCase().trim();
				const searchWords = searchTerm.split(' ').filter(word => word.length > 0);

				filtered = filtered.filter(training => {
					if (searchWords.some(word => word.startsWith('tca'))) {
						return (training.formationName && training.formationName.startsWith('11.2.6.2')) || (training.formationName && training.formationName.toLowerCase().includes('tca'));
					}

					if (searchWords.some(word => word.startsWith('pack'))) {
						return training.title && training.title.includes('+');
					}

					// Cas spécial pour "sur" ou "sûr"
					if (searchWords.some(word => word.startsWith('sur') || word.startsWith('sûr'))) {
						return training.formationName && training.formationName.startsWith('11.2.3');
					}

					// Concatène tous les champs recherchables
					const searchableText = [
						training.formationName,
						training.longTitle,
						training.title,
						training.description
					]
						.filter(field => field)
						.join(' ')
						.toLowerCase();

					// Vérifie que tous les mots de la recherche sont présents
					return searchWords.every(word => searchableText.includes(word));
				});
			}

			return [...filtered].sort((a, b) => a.order - b.order);
		},

		totalPrice() {
			return parseFloat(
				this.cart.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)
			);
		},
	},

	watch: {
		activeCategory() {
			this.$nextTick(() => this.initTooltips());
		},
	},

	async mounted() {
		await this.fetchCompany();
		if (this.company && this.company.name) {
			const name = this.company.name.toUpperCase();
			if (name.includes("SGS") || name.includes("ALLTRANSPACK"))
				this.activeCategory = "dgr";
		}
		this.fetchTrainings();
		this.fetchEmployees();
		this.fetchFeatureFlag();
	},
};
</script>

<style scoped>
.load-center {
	display: block;
	margin: 0 auto;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.modal-simple .modal-content {
	padding: 3rem;
}

.pb-20 {
	padding-bottom: 20px;
}

.row-grey {
	background-color: #f5f5f5;
	padding: 12px;
	border-radius: 6px;
}

</style>
